import { t } from "ttag";

import { useHasTokenFeature } from "metabase/common/hooks";
import type { PaletteSubPath } from "metabase/palette/types";
import {
  PLUGIN_FEATURE_LEVEL_PERMISSIONS,
  PLUGIN_WORKSPACES,
} from "metabase/plugins";
import { useSelector } from "metabase/redux";
import { canAccessTransforms as canAccessTransformsSelector } from "metabase/transforms/selectors";
import * as Urls from "metabase/urls";

// Mirrors DataStudioLayout's own tab list and visibility conditions
// (frontend/src/metabase/data-studio/app/pages/DataStudioLayout/DataStudioLayout.tsx)
// so every section reachable by browsing the Data Studio sidebar is also
// reachable by name from the global command palette -- confirmed live that
// none of them had a second way in before this (WCAG 2.4.5 Multiple Ways).
export const useDataStudioPaths = (): PaletteSubPath[] => {
  const canAccessDataModel = useSelector(
    PLUGIN_FEATURE_LEVEL_PERMISSIONS.canAccessDataModel,
  );
  const canAccessTransforms = useSelector(canAccessTransformsSelector);
  const canManageWorkspaces = useSelector(
    PLUGIN_WORKSPACES.canManageWorkspaces,
  );
  const hasLibraryFeature = useHasTokenFeature("library");
  const hasSchemaViewerFeature = useHasTokenFeature("schema-viewer");
  const hasDependenciesFeature = useHasTokenFeature("dependencies");
  const hasRemoteSyncFeature = useHasTokenFeature("remote_sync");

  const paths: (PaletteSubPath | null)[] = [
    hasLibraryFeature
      ? {
          key: "data-studio-library",
          name: t`Library`,
          path: Urls.dataStudioLibrary(),
        }
      : null,
    canAccessDataModel
      ? {
          key: "data-studio-tables",
          name: t`Tables`,
          path: Urls.dataStudioData(),
        }
      : null,
    hasSchemaViewerFeature
      ? {
          key: "data-studio-schema-viewer",
          name: t`Schema viewer`,
          path: Urls.dataStudioSchemaViewer(),
        }
      : null,
    hasDependenciesFeature
      ? {
          key: "data-studio-dependency-graph",
          name: t`Dependency graph`,
          path: Urls.dependencyGraph(),
        }
      : null,
    hasDependenciesFeature
      ? {
          key: "data-studio-dependency-diagnostics",
          name: t`Dependency diagnostics`,
          path: Urls.dependencyDiagnostics(),
        }
      : null,
    canAccessTransforms
      ? {
          key: "data-studio-transforms",
          name: t`Transforms`,
          path: Urls.transformList(),
        }
      : null,
    {
      key: "data-studio-glossary",
      name: t`Glossary`,
      path: Urls.dataStudioGlossary(),
    },
    !hasRemoteSyncFeature
      ? {
          key: "data-studio-git-sync",
          name: t`Set up remote sync`,
          path: Urls.dataStudioGitSync(),
        }
      : null,
    canManageWorkspaces
      ? {
          key: "data-studio-workspaces",
          name: t`Workspaces`,
          path: Urls.workspaces(),
        }
      : null,
    canAccessTransforms
      ? {
          key: "data-studio-jobs",
          name: `${t`Transforms`} - ${t`Jobs`}`,
          path: Urls.transformJobList(),
        }
      : null,
    canAccessTransforms
      ? {
          key: "data-studio-runs",
          name: `${t`Transforms`} - ${t`Runs`}`,
          path: Urls.transformRunList(),
        }
      : null,
  ];

  return paths.filter((path): path is PaletteSubPath => path !== null);
};
